// Human-readable labels + icons for the Workflow Doctor agent tools.
//
// The raw tool names ("getExecutionErrors", "getNodeOutputFromLog") are
// developer-facing and look noisy in the chat UI. This module maps each
// internal name to a friendly label + emoji icon for the live "investigating"
// trace.
//
// Single source of truth — used by ToolTraceLine in DoctorChatPopup. If
// future surfaces need the same mapping, import from here.

var LABELS = {
  listNodes:            { icon: '🗺️',  label: 'Mapping the workflow' },
  getNode:              { icon: '🔍', label: 'Reading a node' },
  getConnections:       { icon: '🔗', label: 'Tracing connections' },
  getVariables:         { icon: '📦', label: 'Checking variables' },
  getNodeOutputFromLog: { icon: '📜', label: 'Reading last output' },
  getExecutionErrors:   { icon: '⚠️',  label: 'Checking errors' },
  checkNodeOutput:      { icon: '🧪', label: 'Validating output' },
  inspectVariableUsage: { icon: '🧭', label: 'Tracking variable usage' },
  runStaticDiagnose:    { icon: '🩺', label: 'Running full diagnostic' },
  runValidatorAudit:    { icon: '✅', label: 'Auditing workflow rules' },
  searchWeb:            { icon: '🌐', label: 'Searching the web' },
  synthesizeFixPlan:    { icon: '🧠', label: 'Drafting fix plan' }
};

var DEFAULT_LABEL = { icon: '⚙️', label: 'Working' };

function labelFor(toolName) {
  return LABELS[toolName] || Object.assign({}, DEFAULT_LABEL, { label: toolName || DEFAULT_LABEL.label });
}

// Pull a short, human-readable subject from the most informative arg of a
// tool call so the trace shows "Reading node Format Slack Message" instead
// of the bare "Reading a node". Returns '' when there's nothing useful to
// surface (the row falls back to just the icon + label).
function detailFromArgs(toolName, args) {
  if (!args || typeof args !== 'object') return '';
  switch (toolName) {
    case 'getNode':              return String(args.nodeId || args.title || '').trim();
    case 'getNodeOutputFromLog': return String(args.nodeId || args.title || '').trim();
    case 'getConnections':       return String(args.nodeId || '').trim();
    case 'inspectVariableUsage': return String(args.variableName || '').trim();
    case 'searchWeb':            return String(args.query || '').trim().slice(0, 60);
    case 'checkNodeOutput':      return String(args.nodeId || '').trim();
    default:                     return '';
  }
}

// Compress a tool result to a one-line outcome ("3 nodes", "no errors",
// "404", etc.). Best-effort — returns '' when the result shape doesn't
// match a known pattern. Caller can show this next to the count badge.
function outcomeFromResult(toolName, result) {
  if (!result) return '';
  if (result.error) return String(result.error).slice(0, 60);
  switch (toolName) {
    case 'listNodes':
      if (Array.isArray(result.nodes)) return result.nodes.length + ' nodes';
      return '';
    case 'getConnections':
      if (Array.isArray(result.incoming) || Array.isArray(result.outgoing)) {
        var inN = (result.incoming || []).length;
        var outN = (result.outgoing || []).length;
        return inN + ' in / ' + outN + ' out';
      }
      return '';
    case 'inspectVariableUsage':
      var rN = Array.isArray(result.readers) ? result.readers.length : 0;
      var wN = Array.isArray(result.writers) ? result.writers.length : 0;
      return rN + ' reader' + (rN === 1 ? '' : 's') + ' / ' + wN + ' writer' + (wN === 1 ? '' : 's');
    case 'getExecutionErrors':
      if (Array.isArray(result.errors)) {
        return result.errors.length === 0 ? 'no errors' : (result.errors.length + ' error' + (result.errors.length === 1 ? '' : 's'));
      }
      return '';
    default:
      return '';
  }
}

// Collapse consecutive identical tool calls into a single trace row with a
// count badge. e.g. [getNode, getNode, getNode] → [{ name: 'getNode', count: 3 }].
// Keeps the trace readable when the agent inspects multiple nodes in a row.
//
// Each input trace may carry { name, detail, outcome }; collapsed rows keep
// the FIRST detail (oldest) and the LAST outcome (newest, since the latest
// call is what's "freshest" for the user to see).
function dedupeConsecutive(traces) {
  if (!Array.isArray(traces) || traces.length === 0) return [];
  var out = [];
  for (var i = 0; i < traces.length; i++) {
    var t = traces[i];
    if (!t || !t.name) continue;
    var last = out[out.length - 1];
    if (last && last.name === t.name) {
      last.count += 1;
      // Append distinct subjects so "Reading a node × 3 (A, B, C)" reads
      // naturally instead of just collapsing to a single subject.
      if (t.detail && last.details.indexOf(t.detail) < 0) last.details.push(t.detail);
      if (t.outcome) last.outcome = t.outcome; // freshest wins
    } else {
      out.push({
        name: t.name,
        count: 1,
        details: t.detail ? [t.detail] : [],
        outcome: t.outcome || ''
      });
    }
  }
  return out;
}

module.exports = {
  labelFor: labelFor,
  detailFromArgs: detailFromArgs,
  outcomeFromResult: outcomeFromResult,
  dedupeConsecutive: dedupeConsecutive,
  LABELS: LABELS
};
